import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import Icon from '../components/Icon';
import KhariBulbul from '../components/KhariBulbul';
import './Branch.css';

/**
 * Filial (kampus) bölməsi.
 *
 * Kimlik yalnız başlıq panelində cəmlənir — bir motiv, bir vurğu rəngi.
 * Qalan hissə bütün filiallarda eynidir ki, sistem vahid qalsın.
 */
const THEMES = {
  KH: {
    motif: true,
    accent: '#8E2A7C',
    deep:   '#4A1744',
    grad:   'linear-gradient(135deg,#4A1744 0%,#7B2A6E 52%,#9C2585 100%)',
    chip:   '#f6ebf4',
    rule:   '#C9A227',
  },
  MAIN: {
    motif: false,
    accent: '#007c82',
    deep:   '#064e52',
    grad:   'linear-gradient(135deg,#064e52 0%,#007c82 55%,#0a9aa1 100%)',
    chip:   '#e0f2f4',
    rule:   '#7CB82F',
  },
};

export default function Branch({ code = 'KH' }) {
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(true);

  const theme = THEMES[code] || THEMES.MAIN;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: list } = await API.get('/branches');
        const branch = (list || []).find(b => b.code === code);
        if (!branch) throw new Error('Filial tapılmadı');
        const { data: sum } = await API.get(`/branches/${branch.id}/summary`);
        if (alive) setData(sum);
      } catch (e) {
        if (alive) setError(e?.response?.data?.message || e.message || 'Yüklənmədi');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [code]);

  if (loading) return <div className="br-state">Yüklənir…</div>;
  if (error) {
    return (
      <div className="br-state br-error">
        <Icon name="warning" size={18} /> {error}
      </div>
    );
  }

  const c = data.counts;
  const tiles = [
    { key: 'users',    label: 'İstifadəçi', value: c.users,         icon: 'users' },
    { key: 'depts',    label: 'Şöbə',       value: c.departments,   icon: 'building' },
    { key: 'requests', label: 'Açıq sorğu', value: c.open_requests, icon: 'clipboard' },
    { key: 'classes',  label: 'Sinif',      value: c.classes,       icon: 'school' },
  ];

  const links = [
    { label: 'Şöbələr və sorğular', to: '/requests',      icon: 'clipboard' },
    { label: 'Elanlar',             to: '/announcements', icon: 'megaphone' },
    { label: 'Təchizat',            to: '/procurement',   icon: 'package' },
    { label: 'Anbar',               to: '/warehouse',     icon: 'warehouse' },
    { label: 'Şagirdlər',           to: '/students',      icon: 'student' },
    { label: 'Müəllimlər',          to: '/teachers',      icon: 'teacher' },
  ];

  const vars = {
    '--br-accent': theme.accent,
    '--br-deep':   theme.deep,
    '--br-grad':   theme.grad,
    '--br-chip':   theme.chip,
    '--br-rule':   theme.rule,
  };

  return (
    <div className="br-page" style={vars}>
      <header className="br-hero">
        <div className="br-hero-badge">
          {theme.motif
            ? <KhariBulbul size={44} />
            : <Icon name="building" size={30} />}
        </div>
        <div className="br-hero-text">
          <p className="br-hero-eyebrow">Kampus</p>
          <h1 className="br-hero-title">{data.branch.short_name || data.branch.name}</h1>
          <p className="br-hero-sub">{data.branch.name}</p>
        </div>
        {theme.motif && (
          <KhariBulbul size={260} variant="mono" className="br-hero-watermark" />
        )}
      </header>

      <div className="br-tiles">
        {tiles.map(t => (
          <div key={t.key} className="br-tile">
            <span className="br-tile-icon"><Icon name={t.icon} size={20} /></span>
            <div>
              <div className="br-tile-value">{t.value}</div>
              <div className="br-tile-label">{t.label}</div>
            </div>
          </div>
        ))}
      </div>

      <h3 className="br-section-title">Modullar</h3>
      <div className="br-links">
        {links.map(l => (
          <button key={l.to} type="button" className="br-link" onClick={() => navigate(l.to)}>
            <span className="br-link-icon"><Icon name={l.icon} size={18} /></span>
            <span className="br-link-label">{l.label}</span>
            <Icon name="arrowRight" size={16} className="br-link-arrow" />
          </button>
        ))}
      </div>
    </div>
  );
}
